import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, MapPin, Users, Euro, ArrowLeft, Check, Home, Images } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetailPage = () => {
  const { eventId } = useParams();
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    birth_date: '',
    birth_place: '',
    email: '',
    passport_number: '',
    whatsapp: '',
    nickname: '',
    motorcycle_choice: 'KTM EXC500',
    own_bike_brand: '',
    own_bike_model: '',
    own_bike_cc: '',
    room_type: 'double',
    roommate: '',
    food_intolerance: 'Nothing',
    food_intolerance_details: '',
    tshirt_size: 'M'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    
    // Track page view (fire and forget)
    axios.post(`${API}/events/${eventId}/track-view`).catch(() => {});
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedTitle = () => {
    if (!event) return '';
    const lang = i18n.language;
    return event[`title_${lang}`] || event.title_it;
  };

  const getLocalizedDescription = () => {
    if (!event) return '';
    const lang = i18n.language;
    return event[`description_${lang}`] || event.description_it;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const registrationData = {
        ...formData,
        event_id: event.id,
        event_title: getLocalizedTitle()
      };

      await axios.post(`${API}/registrations`, registrationData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        surname: '',
        birth_date: '',
        birth_place: '',
        email: '',
        passport_number: '',
        whatsapp: '',
        nickname: '',
        motorcycle_choice: 'KTM EXC500',
        room_type: 'double',
        roommate: '',
        food_intolerance: 'Nothing',
        food_intolerance_details: '',
        tshirt_size: 'M'
      });
      
      // Reload event to update spots
      fetchEvent();
    } catch (error) {
      console.error('Error submitting registration:', error);
      setSubmitError(error.response?.data?.detail || t('registration.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const motorcycleOptions = [
    'KTM EXC500',
    'KTM EXC450',
    'HUSQVARNA FE450',
    'HONDA CRF 450XR',
    'RZR POLARIS 1 DRIVER',
    'RZR POLARIS 2 DRIVERS',
    'Moto Propria'
  ];

  // Determina se è stata scelta una moto a noleggio o propria
  const isOwnBike = formData.motorcycle_choice === 'Moto Propria';
  const isRZR = formData.motorcycle_choice.includes('RZR');
  const isSingleRoom = formData.room_type === 'single';
  
  // Calcola il prezzo BASE in base alla scelta moto (protetto da null check)
  const getBasePrice = () => {
    if (!event) return 0;
    if (isOwnBike) {
      return event.price_own || 0;
    }
    if (isRZR) {
      if (formData.motorcycle_choice === 'RZR POLARIS 1 DRIVER') {
        return event.price_rzr_single || event.price_rent || 0;
      }
      return event.price_rzr_double || event.price_rent || 0;
    }
    return event.price_rent || 0;
  };

  const basePrice = getBasePrice();
  const singleRoomSupplement = (event && isSingleRoom && event.single_room_supplement) ? event.single_room_supplement : 0;
  const totalPrice = basePrice + singleRoomSupplement;
  const depositAmount = Math.round(totalPrice * 0.20);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('events.noEvents')}</h2>
          <Link to="/events" className="text-blue-600 hover:text-blue-700">
            {t('common.back')} {t('nav.events')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={`${getLocalizedTitle()} | Offroad Italia`}
        description={getLocalizedDescription().substring(0, 160)}
        image={event.image_url}
        type="article"
      />
      {/* Hero Image */}
      <div 
        className="h-96 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${event.image_url})` }}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link 
              to="/events"
              className="inline-flex items-center text-white mb-4 hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              {t('common.back')}
            </Link>
            <h1 className="text-5xl font-bold text-white mb-4">{getLocalizedTitle()}</h1>
            <div className="flex items-center text-white text-lg space-x-6">
              <div className="flex items-center">
                <MapPin className="mr-2" size={24} />
                <span>{event.location}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="mr-2" size={24} />
                <span>{event.date_start} - {event.date_end}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              {/* Event Logo */}
              {event.image_url && (
                <div className="flex justify-center mb-8">
                  <img 
                    src={event.image_url} 
                    alt={getLocalizedTitle()}
                    className="max-h-48 w-auto object-contain rounded-lg shadow-md"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('events.details')}</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 whitespace-pre-line">{getLocalizedDescription()}</p>
              </div>

              {event.itinerary && (
                <div className="mt-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('events.itinerary')}</h3>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-line">{event.itinerary}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Registration Form */}
            {showRegistration && event.status !== 'sold_out' && (
              <div className="bg-white rounded-xl shadow-lg p-8" data-testid="registration-form">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('registration.title')}</h2>
                
                {submitSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
                    <Check className="text-green-600 mr-3" size={24} />
                    <span className="text-green-800 font-medium">{t('registration.success')}</span>
                  </div>
                )}

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <span className="text-red-800">{submitError}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('registration.personalInfo')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.name')} *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="registration-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.surname')} *
                        </label>
                        <input
                          type="text"
                          name="surname"
                          value={formData.surname}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="registration-surname"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.birthDate')} *
                        </label>
                        <input
                          type="date"
                          name="birth_date"
                          value={formData.birth_date}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.birthPlace')} *
                        </label>
                        <input
                          type="text"
                          name="birth_place"
                          value={formData.birth_place}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.email')} *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="registration-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.whatsapp')} *
                        </label>
                        <input
                          type="tel"
                          name="whatsapp"
                          value={formData.whatsapp}
                          onChange={handleInputChange}
                          required
                          placeholder="+39..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.passport')} *
                        </label>
                        <input
                          type="text"
                          name="passport_number"
                          value={formData.passport_number}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.nickname')} *
                        </label>
                        <input
                          type="text"
                          name="nickname"
                          value={formData.nickname}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('registration.motorcycle')}</h3>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('registration.motorcycleLabel')} *
                      </label>
                      <select
                        name="motorcycle_choice"
                        value={formData.motorcycle_choice}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        data-testid="motorcycle-select"
                      >
                        {motorcycleOptions.map((bike) => (
                          <option key={bike} value={bike}>{bike}</option>
                        ))}
                      </select>
                    </div>

                    {/* Campi aggiuntivi per Moto Propria */}
                    {isOwnBike && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800 mb-3 font-medium">
                          {i18n.language === 'it' ? 'Inserisci i dettagli della tua moto (necessario per verificare idoneità al percorso):' : i18n.language === 'en' ? 'Enter your motorcycle details (required to verify route suitability):' : i18n.language === 'de' ? 'Geben Sie Ihre Motorraddetails ein (erforderlich zur Überprüfung der Streckeneignung):' : i18n.language === 'fr' ? 'Entrez les détails de votre moto (nécessaire pour vérifier aptitude au parcours):' : 'Ingrese los detalles de su moto (necesario para verificar idoneidad de la ruta):'}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('registration.motorcycleBrand')} *
                            </label>
                            <input
                              type="text"
                              name="own_bike_brand"
                              value={formData.own_bike_brand}
                              onChange={handleInputChange}
                              required={isOwnBike}
                              placeholder="Es: KTM, Honda, Yamaha..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              data-testid="own-bike-brand"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('registration.motorcycleModel')} *
                            </label>
                            <input
                              type="text"
                              name="own_bike_model"
                              value={formData.own_bike_model}
                              onChange={handleInputChange}
                              required={isOwnBike}
                              placeholder="Es: EXC 500, CRF 450..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              data-testid="own-bike-model"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('registration.motorcycleCC')} *
                            </label>
                            <input
                              type="text"
                              name="own_bike_cc"
                              value={formData.own_bike_cc}
                              onChange={handleInputChange}
                              required={isOwnBike}
                              placeholder="Es: 450, 500, 690..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              data-testid="own-bike-cc"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Riepilogo prezzo in base alla scelta */}
                    <div className={`p-3 rounded-lg ${isOwnBike ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                      <p className="text-sm font-medium">
                        {isOwnBike ? (
                          <span className="text-green-800">
                            ✓ {t('events.ownBike')}: <strong>€{event.price_own?.toLocaleString('it-IT') || 'N/A'}</strong>
                          </span>
                        ) : (
                          <span className="text-orange-800">
                            🏍️ {t('events.rentalBike')} ({formData.motorcycle_choice}): <strong>€{basePrice.toLocaleString('it-IT')}</strong>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('registration.room')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.room')} *
                        </label>
                        <select
                          name="room_type"
                          value={formData.room_type}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="double">{t('registration.roomDouble')}</option>
                          <option value="triple">{t('registration.roomTriple')}</option>
                          <option value="single">
                            {t('registration.roomSingle')} {event.single_room_supplement > 0 ? `(+€${event.single_room_supplement})` : ''}
                          </option>
                        </select>
                        {formData.room_type === 'single' && event.single_room_supplement > 0 && (
                          <p className="text-sm text-amber-700 mt-1">
                            Supplemento camera singola: <strong>+€{event.single_room_supplement}</strong>
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.roommate')}
                        </label>
                        <input
                          type="text"
                          name="roommate"
                          value={formData.roommate}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Opzionale"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('registration.food')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.food')} *
                        </label>
                        <select
                          name="food_intolerance"
                          value={formData.food_intolerance}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="registration-food-intolerance"
                        >
                          <option value="Nothing">{t('registration.foodNone')}</option>
                          <option value="Vegetarian">{t('registration.foodVeg')}</option>
                          <option value="Vegan">{t('registration.foodVegan')}</option>
                          <option value="Gluten Free">{t('registration.foodGluten')}</option>
                          <option value="Other">{t('registration.foodOther')}</option>
                        </select>
                        {formData.food_intolerance === 'Other' && (
                          <input
                            type="text"
                            name="food_intolerance_details"
                            value={formData.food_intolerance_details}
                            onChange={handleInputChange}
                            required
                            placeholder={t('registration.foodOtherPlaceholder')}
                            className="w-full px-4 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            data-testid="registration-food-details"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.tshirt')} *
                        </label>
                        <select
                          name="tshirt_size"
                          value={formData.tshirt_size}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('registration.payment')}</h3>
                    <p className="text-gray-700 mb-4">{t('registration.paymentInfo')}</p>

                    {/* Riepilogo prezzo e acconto basato sulla scelta */}
                    {totalPrice > 0 && (
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 rounded" data-testid="deposit-amount-banner">
                        {/* Dettaglio prezzo base */}
                        <p className="text-sm text-gray-700 mb-1">
                          {isOwnBike ? (
                            <>{i18n.language === 'it' ? 'Prezzo con' : i18n.language === 'en' ? 'Price with' : i18n.language === 'de' ? 'Preis mit' : i18n.language === 'fr' ? 'Prix avec' : 'Precio con'} <strong>{t('events.ownBike').toLowerCase()}</strong>:</>
                          ) : (
                            <>{i18n.language === 'it' ? 'Prezzo con' : i18n.language === 'en' ? 'Price with' : i18n.language === 'de' ? 'Preis mit' : i18n.language === 'fr' ? 'Prix avec' : 'Precio con'} <strong>{formData.motorcycle_choice}</strong> ({i18n.language === 'it' ? 'noleggio' : i18n.language === 'en' ? 'rental' : i18n.language === 'de' ? 'Miete' : i18n.language === 'fr' ? 'location' : 'alquiler'}):</>
                          )}
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          € {basePrice.toLocaleString('it-IT')}
                        </p>
                        
                        {/* Supplemento singola se applicabile */}
                        {singleRoomSupplement > 0 && (
                          <div className="mt-2 pt-2 border-t border-amber-300">
                            <p className="text-sm text-gray-700">
                              {t('events.singleRoom')}:
                            </p>
                            <p className="text-lg font-bold text-amber-600">
                              + € {singleRoomSupplement.toLocaleString('it-IT')}
                            </p>
                          </div>
                        )}
                        
                        {/* Totale */}
                        <div className="mt-3 pt-3 border-t-2 border-amber-400">
                          <p className="text-sm text-gray-700 font-semibold">{i18n.language === 'it' ? 'TOTALE' : i18n.language === 'en' ? 'TOTAL' : i18n.language === 'de' ? 'GESAMT' : i18n.language === 'fr' ? 'TOTAL' : 'TOTAL'}:</p>
                          <p className="text-2xl font-bold text-gray-900">
                            € {totalPrice.toLocaleString('it-IT')}
                          </p>
                        </div>
                        
                        <hr className="border-amber-300 my-3" />
                        <p className="text-sm text-gray-700">{i18n.language === 'it' ? 'Acconto richiesto per bloccare il posto' : i18n.language === 'en' ? 'Deposit required to reserve your spot' : i18n.language === 'de' ? 'Anzahlung erforderlich zur Reservierung' : i18n.language === 'fr' ? 'Acompte requis pour réserver votre place' : 'Depósito requerido para reservar tu lugar'}:</p>
                        <p className="text-2xl font-bold text-amber-700 mt-1">
                          20% = € {depositAmount.toLocaleString('it-IT')}
                        </p>
                      </div>
                    )}

                    <div className="space-y-3 mb-4">
                      {settings?.iban_1_number && (
                        <div>
                          <p className="font-medium text-gray-900 mb-1">{settings.iban_1_label || 'IBAN 1'}:</p>
                          <code className="block bg-white px-3 py-2 rounded text-sm" data-testid="iban-1">{settings.iban_1_number}</code>
                        </div>
                      )}
                      {settings?.iban_2_number && (
                        <div>
                          <p className="font-medium text-gray-900 mb-1">{settings.iban_2_label || 'IBAN 2'}:</p>
                          <code className="block bg-white px-3 py-2 rounded text-sm" data-testid="iban-2">{settings.iban_2_number}</code>
                        </div>
                      )}
                      {settings?.iban_holder && (
                        <div>
                          <p className="font-medium text-gray-900 mb-1">{i18n.language === 'it' ? 'Intestatario' : i18n.language === 'en' ? 'Account holder' : i18n.language === 'de' ? 'Kontoinhaber' : i18n.language === 'fr' ? 'Titulaire du compte' : 'Titular de la cuenta'}:</p>
                          <p className="text-gray-700" data-testid="iban-holder">{settings.iban_holder}</p>
                        </div>
                      )}
                    </div>

                    {(event.sumup_url || settings?.default_sumup_url) && (
                      <a
                        href={event.sumup_url || settings.default_sumup_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                        data-testid="sumup-link"
                      >
                        {totalPrice > 0
                          ? `${i18n.language === 'it' ? 'Paga acconto 20%' : i18n.language === 'en' ? 'Pay 20% deposit' : i18n.language === 'de' ? '20% Anzahlung zahlen' : i18n.language === 'fr' ? 'Payer acompte 20%' : 'Pagar depósito 20%'} (€ ${depositAmount.toLocaleString('it-IT')}) →`
                          : `${t('registration.sumup')} →`}
                      </a>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                    data-testid="submit-registration-btn"
                  >
                    {submitting ? t('contact.sending') : t('registration.submit')}
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('events.moreInfo')}</h3>
              
              <div className="space-y-4 mb-6">
                {event.price_rent && (
                  <div className="flex items-start">
                    <Euro className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">{t('events.rentalBike')}</p>
                      <p className="text-2xl font-bold text-blue-600">€{event.price_rent}</p>
                    </div>
                  </div>
                )}
                {event.price_own && (
                  <div className="flex items-start">
                    <Euro className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">{t('events.ownBike')}</p>
                      <p className="text-2xl font-bold text-blue-600">€{event.price_own}</p>
                    </div>
                  </div>
                )}
                {event.price_rzr_single && (
                  <div className="flex items-start">
                    <Euro className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">RZR Polaris (1 driver)</p>
                      <p className="text-2xl font-bold text-blue-600">€{event.price_rzr_single}</p>
                    </div>
                  </div>
                )}
                {event.price_rzr_double && (
                  <div className="flex items-start">
                    <Euro className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">RZR Polaris (2 drivers)</p>
                      <p className="text-2xl font-bold text-blue-600">€{event.price_rzr_double}</p>
                    </div>
                  </div>
                )}

                {event.single_room_supplement > 0 && (
                  <div className="flex items-start border-t pt-3 mt-3">
                    <Home className="text-amber-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">{t('events.singleRoom')}</p>
                      <p className="text-xl font-bold text-amber-600">+€{event.single_room_supplement}</p>
                    </div>
                  </div>
                )}

                {event.spots_available !== null && (
                  <div className="flex items-start">
                    <Users className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">{t('events.spots')}</p>
                      <p className="text-xl font-bold text-gray-900">
                        {event.spots_available}/{event.spots_total}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {event.status !== 'sold_out' && !showRegistration && (
                <button
                  onClick={() => setShowRegistration(true)}
                  className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg mb-3"
                  data-testid="show-registration-btn"
                >
                  {t('events.register')}
                </button>
              )}

              {event.status === 'sold_out' && (
                <div className="bg-red-100 text-red-800 px-6 py-4 rounded-lg font-semibold text-center mb-3">
                  {t('events.soldOut')}
                </div>
              )}

              <a
                href="https://wa.me/393793793030?text=Ciao%2C%20vorrei%20informazioni%20sugli%20eventi%20Offroad%20Italia"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-green-500 text-white px-6 py-4 rounded-lg hover:bg-green-600 transition-colors font-semibold text-lg flex items-center justify-center"
                data-testid="whatsapp-contact-btn"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                {t('contact.whatsapp')}
              </a>

              {/* Pulsante Galleria Foto/Video */}
              <Link
                to={`/gallery?event=${event.id}`}
                className="w-full mt-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-4 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all font-semibold text-lg flex items-center justify-center shadow-lg"
                data-testid="gallery-link-btn"
              >
                <Images className="mr-2" size={24} />
                {t('gallery.photos')} & {t('gallery.videos')}
              </Link>

              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600">
                  {t('events.included')}: hotel, {i18n.language === 'it' ? 'pasti, noleggio moto (se richiesto), assistenza meccanica, tracking satellitare' : i18n.language === 'en' ? 'meals, motorcycle rental (if requested), mechanical assistance, satellite tracking' : i18n.language === 'de' ? 'Mahlzeiten, Motorradverleih (falls gewünscht), mechanische Unterstützung, Satellitentracking' : i18n.language === 'fr' ? 'repas, location de moto (si demandé), assistance mécanique, suivi satellite' : 'comidas, alquiler de moto (si se solicita), asistencia mecánica, seguimiento por satélite'}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
